<template>
  <main class="metadata-qa">
    <section class="metadata-header">
      <div>
        <p class="eyebrow">MSDAT metadata</p>
        <h1>Ask questions about indicator metadata</h1>
        <p class="header-copy">
          Select an indicator to give the assistant the right MSDAT context, then ask about
          definitions, data sources, methodology, related indicators, reporting periods, or
          interpretation notes.
        </p>
      </div>
      <div class="header-status">
        <span><b-icon icon="cpu"></b-icon> Metadata assistant</span>
        <span><b-icon icon="database"></b-icon> MSDAT context</span>
      </div>
    </section>

    <section class="metadata-workspace">
      <aside class="metadata-browser" aria-label="Metadata browser">
        <div class="panel-heading">
          <div>
            <h2>Choose metadata context</h2>
          </div>
          <button class="ghost-button" type="button" @click="clearContext">
            Clear
          </button>
        </div>

        <label class="field-label" for="indicator-search">Search indicators</label>
        <div class="search-box">
          <b-icon icon="search"></b-icon>
          <input
            id="indicator-search"
            v-model.trim="searchTerm"
            type="search"
            placeholder="Search by indicator name or program area"
          />
        </div>

        <div class="list-header">
          <label class="field-label" for="indicator-list">Indicator list</label>
          <span>{{ filteredIndicatorOptions.length }} available</span>
        </div>

        <div
          id="indicator-list"
          class="indicator-list"
          role="listbox"
          :aria-busy="isLoading ? 'true' : 'false'"
        >
          <div v-if="isLoading" class="indicator-loading">
            <b-spinner small></b-spinner>
            <span>Loading indicators...</span>
          </div>

          <template v-else>
            <button
              v-for="indicator in filteredIndicatorOptions"
              :key="indicator.id"
              type="button"
              class="indicator-item"
              :class="{ 'is-active': selectedIndicatorId === indicator.id }"
              @click="selectIndicator(indicator.id)"
            >
              <span class="indicator-item__name">{{ indicator.full_name || indicator.short_name }}</span>
              <span class="indicator-item__area">{{ indicator.program_area || 'Program area not set' }}</span>
            </button>

            <div v-if="!filteredIndicatorOptions.length" class="indicator-empty">
              <b-icon icon="info-circle"></b-icon>
              <p>No indicators match your search.</p>
            </div>
          </template>
        </div>

        <div v-if="!selectedIndicatorId" class="metadata-empty">
          <b-icon icon="info-circle"></b-icon>
          <p>
            You can ask a general metadata question, but choosing an indicator makes the response
            more specific to the indicator.
          </p>
        </div>
      </aside>

      <section class="metadata-right-column" :class="{ 'has-metadata': selectedMetadata || metadataLoading }">
        <section v-if="selectedMetadata || metadataLoading" class="selected-metadata" aria-label="Selected indicator metadata">
          <div v-if="metadataLoading" class="selected-metadata__loading">
            <b-spinner small></b-spinner>
            <span>Loading indicator metadata...</span>
          </div>
          <template v-else-if="selectedMetadata">
            <div class="selected-metadata__heading">
              <p class="selected-metadata__eyebrow">Selected indicator metadata</p>
              <h3>{{ selectedMetadata.short_name || 'Indicator details' }}</h3>
            </div>

            <div class="metadata-summary">
              <div class="summary-row">
                <span>Full name</span>
                <strong>{{ selectedMetadata.full_name || selectedMetadata.short_name || 'Not provided' }}</strong>
              </div>
              <div class="summary-row">
                <span>Program area</span>
                <strong>{{ selectedMetadata.program_area || 'Not provided' }}</strong>
              </div>
              <div class="summary-row">
                <span>Desirable slope</span>
                <strong>{{ selectedMetadata.desirable_slope || 'Not provided' }}</strong>
              </div>
              <div class="summary-row">
                <span>Data sources</span>
                <strong>{{ dataSourceSummary }}</strong>
              </div>
            </div>
          </template>
        </section>

        <section class="qa-panel" aria-label="Metadata question and answer">
          <div class="panel-heading">
            <div>
              <h2>Ask a metadata question</h2>
            </div>
            <span class="context-pill">
              {{ selectedMetadata ? 'Indicator context attached' : 'General metadata mode' }}
            </span>
          </div>

          <div class="suggestions" aria-label="Question suggestions">
            <button
              v-for="suggestion in activeSuggestions"
              :key="suggestion"
              type="button"
              @click="useSuggestion(suggestion)"
            >
              {{ suggestion }}
            </button>
          </div>

          <label class="field-label" for="metadata-question">Question</label>
          <textarea
            id="metadata-question"
            ref="questionInput"
            v-model="question"
            class="question-input"
            rows="5"
            placeholder="Example: What does this indicator measure, and which data sources support it?"
            :disabled="isAsking"
            @input="autoResizeQuestion"
            @keydown.enter.ctrl.prevent="askQuestion"
          ></textarea>

          <div class="ask-actions">
            <button
              type="button"
              class="ask-button"
              :disabled="isAsking || !question.trim()"
              @click="askQuestion"
            >
              <b-spinner v-if="isAsking" small></b-spinner>
              <b-icon v-else icon="cursor-fill"></b-icon>
              {{ isAsking ? 'Generating answer...' : 'Ask' }}
            </button>
            <button
              type="button"
              class="ghost-button"
              :disabled="isAsking || (!answer && !question)"
              @click="resetQuestion"
            >
              Reset
            </button>
          </div>

          <div v-if="errorMessage" class="answer-error" role="alert">
            <b-icon icon="exclamation-triangle"></b-icon>
            <span>{{ errorMessage }}</span>
          </div>

          <article v-if="answer" class="answer-panel">
            <div class="answer-heading">
              <p class="eyebrow">metadata response</p>
              <span>{{ selectedMetadata ? selectedMetadata.short_name : 'General MSDAT metadata' }}</span>
            </div>
            <div class="markdown-body" v-html="renderMarkdown(answer)"></div>
          </article>

          <article v-else class="answer-placeholder">
            <b-icon icon="chat-left-text"></b-icon>
            <p>
              Answers will appear here with the selected metadata context included in the request.
            </p>
          </article>
        </section>
      </section>
    </section>
  </main>
</template>

<script>
import { marked } from 'marked';
import apiServices from '@/modules/data-layer/services/ApiServices';

const METADATA_QA_WEBHOOK = 'https://n8n.e4eweb.space/webhook/7038e292-511b-49ca-94ba-92738219de03';

export default {
  name: 'MetadataQA',
  data() {
    return {
      searchTerm: '',
      selectedIndicatorId: '',
      selectedMetadata: null,
      dataSources: [],
      relatedIndicators: [],
      question: '',
      answer: '',
      errorMessage: '',
      isAsking: false,
      metadataLoading: false,
      isLoadingIndicators: false,
      sessionId: '',
      generalSuggestions: [
        'What metadata is available on MSDAT?',
        'How should I interpret data source methodology fields?',
        'What is the difference between an indicator and a data source?',
      ],
      contextualSuggestions: [
        'What does this indicator measure?',
        'Which data sources support this indicator?',
        'Explain the methodology and reporting period for this indicator.',
      ],
    };
  },
  computed: {
    isLoading() {
      return this.isLoadingIndicators || this.$store.getters.Indicatorloading || false;
    },
    indicatorOptions() {
      const programAreas = this.$store.getters.getprogramArea || [];
      return programAreas.flatMap((area) => (area.children || []).map((indicator) => ({
        id: indicator.id,
        short_name: indicator.short_name,
        full_name: indicator.full_name || '',
        program_area: indicator.program_area || area.name || area.label || '',
      })));
    },
    filteredIndicatorOptions() {
      const search = this.searchTerm.toLowerCase();
      if (!search) return this.indicatorOptions;

      const words = search.split(/\s+/).filter(Boolean);
      return this.indicatorOptions.filter((indicator) => {
        const shortName = (indicator.short_name || '').toLowerCase();
        const fullName = (indicator.full_name || '').toLowerCase();
        const programArea = (indicator.program_area || '').toLowerCase();
        return words.every((word) => shortName.includes(word)
          || fullName.includes(word)
          || programArea.includes(word));
      });
    },
    activeSuggestions() {
      return this.selectedMetadata ? this.contextualSuggestions : this.generalSuggestions;
    },
    dataSourceSummary() {
      if (!this.dataSources.length) return 'Not provided';
      return this.dataSources.map((source) => source.datasource || source.name || source.full_name).filter(Boolean).join(', ');
    },
    metadataContext() {
      return {
        indicator: this.selectedMetadata ? {
          id: this.selectedMetadata.id,
          full_name: this.selectedMetadata.full_name,
          short_name: this.selectedMetadata.short_name,
          program_area: this.selectedMetadata.program_area,
          desirable_slope: this.selectedMetadata.desirable_slope,
          definition: this.selectedMetadata.definition || this.selectedMetadata.description,
          numerator: this.selectedMetadata.numerator,
          denominator: this.selectedMetadata.denominator,
        } : null,
        dataSources: this.dataSources.map((source) => ({
          id: source.id,
          name: source.datasource || source.name,
          full_name: source.full_name,
          description: source.description,
          methodology: source.methodology,
          year_available: source.year_available,
        })),
        relatedIndicators: this.relatedIndicators.map((indicator) => ({
          id: indicator.id,
          short_name: indicator.short_name,
          full_name: indicator.full_name,
          program_area: indicator.program_area,
        })),
        selectedIndicatorLabel: this.selectedIndicatorLabel,
      };
    },
    selectedIndicatorLabel() {
      if (!this.selectedMetadata) return '';
      return this.selectedMetadata.full_name || this.selectedMetadata.short_name || `Indicator ${this.selectedMetadata.id}`;
    },
  },
  watch: {
    indicatorOptions(options) {
      if (options.length && !this.isLoadingIndicators) {
        this.isLoadingIndicators = false;
      }
    },
  },
  created() {
    this.sessionId = this.generateSessionId();
    this.loadIndicators();
  },
  methods: {
    generateSessionId() {
      return 'metadata-qa-xxxxxxxx'.replace(/[x]/g, () => Math.floor(Math.random() * 16).toString(16));
    },
    async loadIndicators() {
      if (this.indicatorOptions.length) return;
      this.isLoadingIndicators = true;
      try {
        await this.$store.dispatch('loadIndicators');
      } catch (error) {
        this.errorMessage = 'Unable to load indicators. You can still ask a general metadata question.';
      } finally {
        this.isLoadingIndicators = false;
      }
    },
    async loadIndicatorMetadata() {
      this.answer = '';
      this.errorMessage = '';
      this.selectedMetadata = null;
      this.dataSources = [];
      this.relatedIndicators = [];

      if (!this.selectedIndicatorId) return;

      this.metadataLoading = true;
      try {
        const response = await apiServices.getSingleIndicatorObj(this.selectedIndicatorId);
        this.selectedMetadata = response.data || null;

        const datasourceResponse = await apiServices.getIndicatorDatasources(this.selectedIndicatorId);
        const datasourceIds = this.normalizeIdList(datasourceResponse && datasourceResponse.data);
        const relatedIds = [
          this.selectedMetadata && this.selectedMetadata.first_related,
          this.selectedMetadata && this.selectedMetadata.second_related,
          this.selectedMetadata && this.selectedMetadata.third_related,
        ].filter(Boolean);

        const datasourceResponses = await Promise.all(
          (datasourceIds.length ? datasourceIds : this.normalizeIdList(this.selectedMetadata && this.selectedMetadata.datasources))
            .map((id) => apiServices.getSingleDataSourceObj(id).catch(() => null)),
        );
        this.dataSources = datasourceResponses.map((item) => item && item.data).filter(Boolean);

        const relatedResponses = await Promise.all(
          relatedIds.map((id) => apiServices.getSingleIndicatorObj(id).catch(() => null)),
        );
        this.relatedIndicators = relatedResponses.map((item) => item && item.data).filter(Boolean);
      } catch (error) {
        this.errorMessage = 'Unable to load metadata for the selected indicator.';
      } finally {
        this.metadataLoading = false;
      }
    },
    selectIndicator(indicatorId) {
      if (this.selectedIndicatorId === indicatorId && this.selectedMetadata) return;
      this.selectedIndicatorId = indicatorId;
      this.loadIndicatorMetadata();
    },
    normalizeIdList(value) {
      if (!value) return [];
      if (Array.isArray(value)) return value.map((item) => (typeof item === 'object' ? item.id : item)).filter(Boolean);
      return [value];
    },
    useSuggestion(suggestion) {
      this.question = suggestion;
      this.$nextTick(() => {
        this.autoResizeQuestion();
        if (this.$refs.questionInput) this.$refs.questionInput.focus();
      });
    },
    async askQuestion() {
      if (!this.question.trim() || this.isAsking) return;

      this.isAsking = true;
      this.answer = '';
      this.errorMessage = '';

      try {
        const chatInput = this.selectedMetadata
          ? `Selected indicator: ${this.selectedIndicatorLabel}\nQuestion: ${this.question.trim()}`
          : this.question.trim();

        const response = await fetch(METADATA_QA_WEBHOOK, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sessionId: this.sessionId,
            chatInput,
            page: 'metadata-qa',
            intent: 'msdat_metadata_question',
            metadataContext: this.metadataContext,
          }),
        });

        const raw = await response.text();
        const data = this.parseResponse(raw);

        if (!response.ok) {
          throw new Error((data && (data.error || data.message)) || raw || `${response.status} ${response.statusText}`);
        }

        this.answer = this.extractAnswer(data, raw);
      } catch (error) {
        this.errorMessage = error && error.message
          ? error.message
          : 'Unable to get a response from the metadata assistant.';
      } finally {
        this.isAsking = false;
      }
    },
    parseResponse(raw) {
      try {
        return raw ? JSON.parse(raw) : null;
      } catch (error) {
        return null;
      }
    },
    extractAnswer(data, raw) {
      if (!data) return raw || 'No response was returned.';
      if (typeof data === 'string') return data;
      return data.text || data.message || data.response || data.answer || raw || 'No response was returned.';
    },
    resetQuestion() {
      this.question = '';
      this.answer = '';
      this.errorMessage = '';
      this.$nextTick(() => this.autoResizeQuestion());
    },
    clearContext() {
      this.selectedIndicatorId = '';
      this.selectedMetadata = null;
      this.dataSources = [];
      this.relatedIndicators = [];
      this.answer = '';
      this.errorMessage = '';
    },
    autoResizeQuestion() {
      this.$nextTick(() => {
        const input = this.$refs.questionInput;
        if (input) {
          input.style.height = 'auto';
          input.style.height = `${Math.min(input.scrollHeight, 220)}px`;
        }
      });
    },
    renderMarkdown(text) {
      return marked.parse(text || '');
    },
  },
};
</script>

<style lang="scss" scoped>
.metadata-qa {
  min-height: 100vh;
  padding: 32px;
  font-family: 'Work Sans', sans-serif;
  color: #162033;
  background: #f3f7f4;
}

.metadata-qa p:not(.eyebrow) {
  font-size: 1rem;
  line-height: 1.7;
}

.metadata-header {
  max-width: 1380px;
  margin: 0 auto 24px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 24px;
  padding: 22px 24px;
  border: 1px solid #d9e4de;
  border-radius: 20px;
  background: #0f7b64;
  color: #f4fbf8;
  box-shadow: 0 10px 24px rgba(8, 48, 40, 0.12);

  h1 {
    margin: 4px 0 10px;
    font-size: 2rem;
    line-height: 1.15;
    font-weight: 800;
    letter-spacing: -0.04em;
  }
}

.eyebrow {
  margin: 0;
  color: inherit;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.header-copy {
  max-width: 760px;
  margin: 0;
  color: rgba(244, 251, 248, 0.82);
  font-size: 1rem;
  line-height: 1.6;
}

.header-status {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;

  span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: 1px solid rgba(255, 255, 255, 0.16);
    background: rgba(255, 255, 255, 0.08);
    border-radius: 999px;
    padding: 8px 10px;
    color: #ffffff;
    font-size: 0.8rem;
    font-weight: 600;
  }
}

.metadata-workspace {
  max-width: 1380px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: minmax(300px, 360px) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.metadata-right-column {
  display: grid;
  gap: 20px;

  &.has-metadata {
    @media (min-width: 901px) {
      grid-template-columns: minmax(280px, 350px) 1fr;
      align-items: start;
    }
  }
}

.metadata-browser,
.qa-panel {
  background: #ffffff;
  border: 1px solid #d9e4de;
  border-radius: 20px;
  padding: 22px;
  box-shadow: 0 8px 18px rgba(22, 32, 51, 0.05);
}

.metadata-browser {
  @media (min-width: 901px) {
    position: sticky;
    top: 24px;
  }
}

.panel-heading {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 18px;

  h2 {
    margin: 4px 0 0;
    font-size: 1.05rem;
    line-height: 1.3;
    letter-spacing: -0.02em;
  }
}

.field-label {
  display: block;
  margin: 14px 0 8px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #405063;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d4ddd8;
  border-radius: 12px;
  padding: 0 12px;
  color: #6a7c78;
  background: #ffffff;

  input {
    width: 100%;
    border: 0;
    outline: 0;
    padding: 11px 0;
    color: #162033;
    background: transparent;
  }
}

.list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-top: 14px;

  span {
    color: #5f7370;
    font-size: 0.76rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
}

.indicator-list {
  display: grid;
  gap: 10px;
  max-height: 450px;
  overflow: auto;
  padding-right: 4px;
  margin-top: 10px;
}

.indicator-item {
  display: grid;
  gap: 4px;
  width: 100%;
  text-align: left;
  border: 1px solid #d9e4de;
  border-radius: 14px;
  background: #ffffff;
  padding: 14px 15px;
  cursor: pointer;
  transition: border-color 120ms ease, background-color 120ms ease;

  &:hover {
    border-color: #b7cec4;
    background: #f8fbf9;
  }

  &.is-active {
    border-color: #0f7b64;
    background: #eef7f3;
  }
}

.indicator-item__name {
  color: #103f35;
  font-size: 0.94rem;
  font-weight: 800;
  line-height: 1.35;
}

.indicator-item__area {
  color: #5f7370;
  font-size: 0.78rem;
  line-height: 1.35;
}

.indicator-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  color: #0f7b64;
  font-weight: 600;
  font-size: 0.9rem;
}

.indicator-empty {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 16px;
  border: 1px dashed #c9d7d0;
  border-radius: 14px;
  background: #fbfcfb;
  color: #5f7370;

  p {
    margin: 0;
    line-height: 1.5;
  }
}

.selected-metadata {
  margin-top: 0;
  padding: 16px;
  border: 1px solid #c8d8d0;
  border-radius: 16px;
  background: #f7fbf9;
  box-shadow: inset 0 1px 0 #ffffff;

  @media (min-width: 901px) {
    position: sticky;
    top: 24px;
  }
}

.selected-metadata__loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 32px 16px;
  color: #0f7b64;
  font-weight: 600;
  font-size: 0.9rem;
}

.selected-metadata__heading {
  margin-bottom: 12px;

  h3 {
    margin: 4px 0 0;
    color: #103f35;
    font-size: 1rem;
    line-height: 1.35;
    font-weight: 800;
  }
}

.selected-metadata__eyebrow {
  margin: 0;
  color: #0f7b64;
  font-size: 0.72rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.question-input {
  min-height: 132px;
  max-height: 220px;
  resize: none;
  padding: 12px;
  line-height: 1.55;
  width: 100%;
  border: 1px solid #d4ddd8;
  border-radius: 14px;
  color: #162033;
  background: #ffffff;
  outline: 0;

  &:focus {
    border-color: #0f7b64;
    box-shadow: 0 0 0 2px rgba(15, 123, 100, 0.1);
  }
}
.metadata-state,
.metadata-empty,
.answer-placeholder,
.answer-error {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  color: #526071;
  background: #f7fafc;
  border: 1px solid #e4ebf1;
  border-radius: 14px;
  padding: 14px;
  margin-top: 16px;

  p {
    margin: 0;
    line-height: 1.5;
  }
}

.metadata-empty p,
.answer-placeholder p,
.indicator-empty p,
.metadata-state span,
::v-deep .markdown-body p,
::v-deep .markdown-body ul,
::v-deep .markdown-body ol {
  font-size: 1rem;
}

.metadata-summary {
  margin-top: 0;
  border-top: 1px solid #dde7e1;
}

.summary-row {
  padding: 13px 0;
  border-bottom: 1px solid #e8eef3;

  span {
    display: block;
    color: #5f7370;
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: #162033;
    line-height: 1.45;
    font-size: 0.92rem;
  }
}

.suggestions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 18px;

  button {
    border: 1px solid #d4ddd8;
    background: #ffffff;
    color: #0f4a3f;
    border-radius: 999px;
    padding: 9px 12px;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    transition: border-color 120ms ease, background-color 120ms ease;

    &:hover {
      border-color: #b7cec4;
      color: #0f7b64;
      background: #f8fbf9;
    }
  }
}

.context-pill {
  white-space: nowrap;
  border-radius: 999px;
  background: #eef7f3;
  color: #0f7b64;
  padding: 7px 10px;
  font-size: 0.75rem;
  font-weight: 700;
}

.ask-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}

.ask-button,
.ghost-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 6px;
  min-height: 38px;
  padding: 0 14px;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.58;
  }
}

.ask-button {
  border: 1px solid #0f7b64;
  background: #0f7b64;
  color: #ffffff;
  box-shadow: none;
}

.ghost-button {
  border: 1px solid #d4ddd8;
  background: #ffffff;
  color: #405063;
}

.answer-error {
  color: #8a3a00;
  border-color: #ffd7a8;
  background: #fff8f0;
}

.answer-panel {
  margin-top: 20px;
  border: 1px solid #d9e4de;
  border-radius: 16px;
  overflow: hidden;
}

.answer-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  background: #f7faf8;
  border-bottom: 1px solid #d9e4de;
  padding: 12px 14px;

  span {
    color: #5f7370;
    font-size: 0.8rem;
    font-weight: 700;
  }
}

.answer-placeholder {
  min-height: 120px;
  align-items: center;
  margin-top: 20px;
}

::v-deep .markdown-body {
  padding: 18px;
  color: #162033;
  line-height: 1.65;

  p,
  ul,
  ol {
    margin-bottom: 0.85rem;
  }

  :last-child {
    margin-bottom: 0;
  }

  strong {
    color: #103f35;
  }
}

@media (max-width: 900px) {
  .metadata-qa {
    padding: 20px;
  }

  .metadata-header {
    align-items: flex-start;
    flex-direction: column;
  }

  .header-status {
    justify-content: flex-start;
  }

  .metadata-header,
  .metadata-browser,
  .qa-panel {
    border-radius: 16px;
  }

  .metadata-workspace {
    grid-template-columns: 1fr;
  }

  .metadata-right-column {
    gap: 16px;
  }

  .list-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
